// src/routes/privateRoutes.js
// General routes accessible to all user types + route combination

import React from "react";
import { Navigate } from "react-router-dom";

// General app page imports (accessible to all user types)
import Feed from "../pages/Feed";
import FeedProfile from "../pages/FeedProfile";
import Notifications from "../pages/notifications";
import Chat from "../pages/chat";
import ChatRoom from "../pages/chatRoom";
import Profile from "../pages/user/profile";
import CompleteProfile from "../pages/user/completeProfile";
import CompleteProfilePhone from "../pages/user/completeProfilePhone";
import CompleteProfileGender from "../pages/user/completeProfileGender";
import CompleteProfileBirthday from "../pages/user/completeProfileBirthday";
import CompleteProfileDocuments from "../pages/user/completeProfileDocuments";

// Import user-type specific routes
import { organizationRoutes } from "./organizationRoutes";
import { playerRoutes } from "./playerRoutes";
import { supervisorRoutes } from "./SupervisorRoutes";
import { talentScoutRoutes } from "./talentScoutRoutes";
import { parentRoutes } from "./parentRoutes";

// Import auth context for user type detection
import { useAuth } from "../contexts/authContext";

// Root redirect component based on user type
const RootRedirect = () => {
  const { user } = useAuth();

  if (!user) return <Navigate to="/signin" replace />;

  // Profile completion is now handled by PrivateRoute.js
  // This component only handles the main app flow after all checks pass

  switch (user.accountType) {
    case "organization": {
      if (
        user.metaData?.subscription?.isActive &&
        new Date(user.metaData.subscription.endDate) > new Date()
      ) {
        return <Navigate to="/organization/dashboard" replace />;
      } else {
        return <Navigate to="/organization/subscription/packages" replace />;
      }
    }
    case "supervisor":
      return <Navigate to="/supervisor/dashboard" replace />;
    case "player":
      return <Navigate to="/feed" replace />;
    case "talent-scout":
      return <Navigate to="/feed" replace />;
    case "parent":
      return <Navigate to="/parent/children" replace />;
    default:
      return <Navigate to="/complete-profile" replace />;
  }
};

// General routes accessible to all user types
const generalRoutes = [
  { path: "/", element: <RootRedirect /> },
  { path: "/complete-profile", element: <CompleteProfile /> },
  { path: "/complete-profile/phone", element: <CompleteProfilePhone /> },
  { path: "/complete-profile/gender", element: <CompleteProfileGender /> },
  { path: "/complete-profile/birthday", element: <CompleteProfileBirthday /> },
  { path: "/complete-profile/documents", element: <CompleteProfileDocuments /> },
  { path: "/feed", element: <Feed /> },
  { path: "/feed/:id", element: <FeedProfile /> },
  { path: "/notifications", element: <Notifications /> },
  { path: "/chat", element: <Chat /> },
  { path: "/chat/:id", element: <ChatRoom /> },
  { path: "/chat/team/:id", element: <ChatRoom /> },
  { path: "/user/profile", element: <Profile /> },
];

// Combine all routes
export const privateRoutes = [
  ...generalRoutes,
  ...organizationRoutes,
  ...playerRoutes,
  ...supervisorRoutes,
  ...talentScoutRoutes,
  ...parentRoutes,
];
